import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import useMultiselectFilter from "../hooks/useMultiselectFilter";
import { useServerSearch } from "../hooks/useServerSearch";
import { selectedFiltersContext } from ".";
import FilterOptionIcon from "../productListingFiltersWeb/filterOptionIcon";
import { FilterOption, GeneralFacet } from "../types";
import SquareRadio from "@/components/squareRadio";

type FilterMultiSelectProps = {
  filter: Omit<GeneralFacet & { data: FilterOption[] }, "sortOrder">;
};

const FilterMultiSelectMob: FC<FilterMultiSelectProps> = ({ filter }) => {
  const [searchValue, setSearchValue] = useState("");
  const { updateSelectedFilters, selectedFilters } = useContext(
    selectedFiltersContext
  );
  const {
    filterOptions,
    selectedOptions,
    onFilterOptionClick,
    setSelectedOptions
  } = useMultiselectFilter(filter, payload => updateSelectedFilters(payload));
  useEffect(() => {
    const fromContext =
      selectedFilters?.[filter.queryParam]?.selectedOptions || [];
    setSelectedOptions(prev =>
      isEqual(prev, fromContext) ? prev : fromContext
    );
  }, [selectedFilters, filter.queryParam]);

  useEffect(() => {
    setSearchValue("");
  }, [filter.queryParam]);

  const handleSearchType = (e: any) => {
    setSearchValue(e.target.value.toLocaleLowerCase());
  };

  const { isServerSearch, serverOptions, isSearchable } = useServerSearch(
    filter,
    searchValue
  );
  const isStoreFilter = filter.queryParam === "store";

  const clearSearchInput = () => {
    searchValue && setSearchValue("");
  };

  const optionItem = (option, idx) => {
    const isChecked = selectedOptions.some(el => el.key === option.key);
    return (
      <li
        className={"filter_option"}
        key={`${option.key}-${idx}`}
        onClick={e => {
          e.stopPropagation();
          onFilterOptionClick({ option }, e);
        }}
      >
        <SquareRadio isChecked={isChecked} name={option.key} />
        {option.icon && !isStoreFilter && (
          <FilterOptionIcon icon={option.icon} />
        )}
        <span id={idx.toString()}>
          {isStoreFilter ? `${option.label} (${option.count})` : option.label}
        </span>
        {option.icon && isStoreFilter ? (
          <FilterOptionIcon icon={option.icon} />
        ) : (
          <span>({option.count})</span>
        )}
      </li>
    );
  };

  const optionsToRender: any[] = useMemo(() => {
    if (isServerSearch) {
      if (serverOptions === null) {
        return filterOptions;
      }
      if (!isEmpty(serverOptions) && !isEmpty(searchValue)) {
        return serverOptions;
      }
      return serverOptions;
    }

    return filterOptions.filter(option =>
      option.label.toLocaleLowerCase().includes(searchValue)
    );
  }, [isServerSearch, filterOptions, serverOptions, searchValue]);

  const filterOptionList = () => {
    switch (filter.viewType) {
      case "list":
        return optionsToRender.map((option, index) =>
          optionItem(option, index)
        );
      case "groups": {
        const hasGroupStructure =
          optionsToRender.length > 0 && optionsToRender[0]?.data !== undefined;

        if (isServerSearch && !isEmpty(searchValue) && !hasGroupStructure) {
          return optionsToRender.map((option, index) =>
            optionItem(option, index)
          );
        }

        return filterOptions.map((group, groupIndex) => {
          const filteredGroupData = group.data.filter(option =>
            option.label.toLocaleLowerCase().includes(searchValue)
          );

          if (filteredGroupData.length === 0) {
            return null;
          }

          return (
            <li className="filter_options_type" key={groupIndex}>
              <span className="filter_type_label">{group.label}</span>
              <ul>
                {filteredGroupData.map((option, index) =>
                  optionItem(option, index)
                )}
              </ul>
            </li>
          );
        });
      }
      default:
        return optionsToRender.map((option, index) =>
          optionItem(option, index)
        );
    }
  };

  return (
    <div className="filter_content" onClick={e => e.stopPropagation()}>
      {isSearchable && (
        <div className="search-wrapper">
          <input
            type="text"
            placeholder={filter.search.placeholder}
            value={searchValue}
            onChange={handleSearchType}
            aria-label={`Search for ${filter.label}`}
          />
          <span className="search_icon" />
          {searchValue && (
            <span className="cross_icon" onClick={clearSearchInput} />
          )}
        </div>
      )}
      <div className="filter_content_scroll_host">
        <div className="filter_content_scroll_area">
          <Scrollbars style={{ width: "100%", height: "100%" }}>
            <ul className="filter_options_list">{filterOptionList()}</ul>
          </Scrollbars>
        </div>
      </div>
    </div>
  );
};

export default FilterMultiSelectMob;
